<template>
    <drawer :closable="true" placement="right" title="Aprovações" :value="value" @input="$emit('input', $event)"
            width="600" class="drawer-approvals">
        <Collapse simple v-model="panel">
            <Panel name="pending">
                Pendentes de aprovação
                <span v-if="pending.length" class="badge-pending">{{pending.length}}</span>
                <div slot="content">
                    <p v-if="!pending.length" class="empty">Nenhuma solicitação aguardando aprovação.</p>
                    <div v-for="item in pending" :key="item.id" class="request">
                        <div class="header">
                            <b>{{item.nome_pessoa_registro}}</b>
                            <span class="target">{{item.server}} / {{item.database}}</span>
                        </div>
                        <div class="date">{{formatDate(item.created_at)}}</div>
                        <pre class="command">{{item.command}}</pre>
                        <div class="reason"><b>Motivo:</b> {{item.reason}}</div>
                        <div class="flex actions">
                            <vs-button size="small" color="success" icon-pack="feather" icon="icon-check"
                                       :disabled="processing" @click="confirmApproval(item)">Aprovar
                            </vs-button>
                            <vs-button size="small" color="danger" type="border" icon-pack="feather" icon="icon-x"
                                       :disabled="processing" class="ml-2" @click="openRejection(item)">Rejeitar
                            </vs-button>
                        </div>
                    </div>
                </div>
            </Panel>
            <Panel name="mine">
                Minhas solicitações
                <div slot="content">
                    <p v-if="!mine.length" class="empty">Você ainda não enviou solicitações.</p>
                    <div v-for="item in mine" :key="item.id" class="request">
                        <div class="header">
                            <span :class="['status', 'status-' + item.status.toLowerCase()]">{{item.status}}</span>
                            <span class="target">{{item.server}} / {{item.database}}</span>
                        </div>
                        <div class="date">{{formatDate(item.created_at)}}</div>
                        <pre class="command">{{item.command}}</pre>
                        <div v-if="item.approver_name" class="reason">
                            <b>{{item.status === 'REJECTED' ? 'Rejeitada por:' : 'Aprovada por:'}}</b>
                            {{item.approver_name}}
                        </div>
                        <div v-if="item.rejection_reason" class="reason">
                            <b>Motivo da rejeição:</b> {{item.rejection_reason}}
                        </div>
                        <div v-if="item.message" class="reason"><b>Retorno:</b> {{item.message}}</div>
                        <div v-if="item.status === 'BYPASSED'" class="reason">
                            <b>Executada direto</b>, sem aprovação
                        </div>
                        <div v-if="item.affected_rows !== null && ['APPROVED', 'BYPASSED'].includes(item.status)"
                             class="reason">
                            <b>Linhas afetadas:</b> {{item.affected_rows}}
                        </div>
                    </div>
                </div>
            </Panel>
        </Collapse>

        <Modal v-model="rejection.show" title="Rejeitar solicitação" ok-text="Rejeitar" cancel-text="Cancelar"
               @on-ok="confirmRejection">
            <p class="mb-2">Explique para o solicitante por que a query não pode ser executada.</p>
            <i-input type="textarea" :rows="4" v-model="rejection.reason" placeholder="Motivo da rejeição"/>
        </Modal>
    </drawer>
</template>

<script>
    import moment from "moment";

    export default {
        name: "ApprovalsDrawer",
        props: {
            value: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                panel: ['pending'],
                pending: [],
                mine: [],
                processing: false,
                rejection: {
                    show: false,
                    reason: '',
                    item: null
                }
            }
        },
        methods: {
            async load() {
                try {
                    const {data: {pending, mine}} = await this.$axios.get('queries/approvals');
                    this.pending = pending;
                    this.mine = mine;
                    this.$emit('updated', pending.length);
                } catch ({message}) {
                    this.notifyError(message);
                }
            },
            /**
             * Confirmação obrigatória: aprovar executa o comando de verdade, com as
             * credenciais de banco de quem aprova.
             */
            confirmApproval(item) {
                this.$Modal.confirm({
                    title: 'Aprovar e executar',
                    content: `<p>O comando abaixo será executado agora em <b>${item.server} / ${item.database}</b>,`
                        + ` solicitado por <b>${item.nome_pessoa_registro}</b>.</p>`
                        + `<pre style="white-space:pre-wrap;margin-top:10px">${this.escapeHtml(item.command)}</pre>`,
                    okText: 'Executar',
                    cancelText: 'Cancelar',
                    onOk: () => this.approve(item)
                });
            },
            async approve(item) {
                this.processing = true;
                this.$vs.loading({scale: 0.6});
                try {
                    const {data} = await this.$axios.post(`queries/approvals/${item.id}/approve`);
                    this.$emit('executed', data);
                    this.$vs.notify({
                        title: 'Query executada',
                        text: `${data.affected_rows} linha(s) afetada(s) em ${data.time.toFixed(4)} ms`,
                        color: "#222222",
                        time: 10000,
                        position: 'top-right'
                    });
                    await this.load();
                } catch ({message}) {
                    this.notifyError(message);
                    await this.load();
                }
                this.$vs.loading.close();
                this.processing = false;
            },
            openRejection(item) {
                this.rejection = {show: true, reason: '', item};
            },
            async confirmRejection() {
                const {item, reason} = this.rejection;
                if (!reason || reason.trim().length < 5) {
                    return this.notifyError('Informe o motivo da rejeição (mínimo 5 caracteres).');
                }
                this.processing = true;
                try {
                    await this.$axios.post(`queries/approvals/${item.id}/reject`, {
                        rejection_reason: reason
                    });
                    this.$vs.notify({
                        text: 'Solicitação rejeitada.',
                        color: "#222222",
                        position: 'top-right'
                    });
                    await this.load();
                } catch ({message}) {
                    this.notifyError(message);
                }
                this.processing = false;
            },
            notifyError(message) {
                this.$vs.notify({
                    title: 'Erro',
                    text: message,
                    color: "rgb(242, 19, 93)",
                    time: 10000,
                    position: 'top-right'
                });
            },
            formatDate(date) {
                return date ? moment(date).format('DD/MM/YYYY HH:mm') : '';
            },
            escapeHtml(text) {
                return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            }
        },
        watch: {
            value(opened) {
                if (opened) {
                    this.load();
                }
            }
        }
    }
</script>

<style lang="scss">
    .drawer-approvals {
        .empty {
            padding: 10px 5px;
            color: #999;
        }

        .request {
            padding: 10px 5px 15px;
            border-bottom: 1px solid #eee;

            .header {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }

            .target {
                font-size: 11px;
                color: #666;
            }

            .date {
                font-size: 11px;
                color: #999;
                margin-bottom: 5px;
            }

            .command {
                background: #f7f7f7;
                border-radius: 4px;
                padding: 8px;
                font-size: 12px;
                white-space: pre-wrap;
                word-break: break-word;
                max-height: 180px;
                overflow: auto;
                margin: 0 0 5px;
            }

            .reason {
                font-size: 12px;
                margin-bottom: 3px;
                word-break: break-word;
            }

            .actions {
                margin-top: 8px;
            }
        }

        .badge-pending {
            background: rgb(242, 134, 32);
            color: #fff;
            border-radius: 10px;
            padding: 0 7px;
            font-size: 11px;
            margin-left: 6px;
        }

        .status {
            font-size: 11px;
            font-weight: bold;
            border-radius: 3px;
            padding: 2px 6px;
            color: #fff;
            background: #999;

            &.status-pending { background: rgb(242, 134, 32); }
            &.status-approved { background: #00b894; }
            &.status-rejected { background: rgb(242, 19, 93); }
            &.status-error { background: rgb(242, 19, 93); }
            &.status-expired { background: #7f8c8d; }
            &.status-bypassed { background: #6c5ce7; }
        }
    }
</style>
